import React, { FC, useEffect, useMemo, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { getSessionIdCookie, setPromoCodeCookie } from '@utils/Session.utils';
import { useDispatch, useSelector } from 'react-redux';
import CarDetailsDuck from '../../redux/carDetails/carDetails.duck';
import FinanceWidgetDuck from '../../redux/financeWidget/financeWidget.duck';
import Icon, { Icons } from '../Icon';
import Spinner from '../Spinner';
import GlobalDuck from '../../redux/global/global.duck';
import { DealService } from '../../services';
import { PromoCodeProps } from '@components/PromoCodeInput/index';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { hasMonthlyPriceByType } from '@utils/Price.utils';
import { EMPLOYEE_PRICE_TYPE } from '../../services/filters/filters';
import { Pages } from '../../types/pages';
import { useGTM } from '@hooks/useGTM';
import { GtmData } from 'src/types/gtm';
import { useUserDuck } from '@hooks/useUserDuck';
import {useGlobalDuck} from "@hooks/useGlobalDuck";

enum ERROR_MESSAGE_IDS {
    AGENT_DISCOUNT_APPLIED = 'AGENT_DISCOUNT_APPLIED',
}

const PromoCodeTemplate: FC<PromoCodeProps> = ({
    className,
    showTitle = true,
    page,
    validateSelectedPaymentMethod = false,
}) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { currentDeal } = useCarDetailsDuck();
    const { pushToDataLayer } = useGTM();
    const { isUserLoggedInAsDealer } = useUserDuck();

    const appliedPromoCode = useSelector(
        (state) => CarDetailsDuck.getOwnState(state)?.currentDeal?.extraFields
    )?.promoCode;
    const appliedParsedPromoCode = appliedPromoCode && JSON.parse(appliedPromoCode);

    const { promoCodeError } = useGlobalDuck();

    const [promoCode, setPromoCode] = useState(null);
    const [promoCodeErrorMessage, setPromoCodeErrorMessage] = useState(null);
    const [isVisibleSuccessMessage, setIsVisibleSuccessMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const buttonLabel = useMemo(() => t('promoCode.button.apply'), [t]);

    useEffect(() => {
        if (!appliedParsedPromoCode) {
            setIsVisibleSuccessMessage(null);
        }
    }, [currentDeal]);

    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
    const applyPromoCode = async () => {
        // 1. Check if customer has applied promo code
        if (appliedParsedPromoCode) {
            setPromoCodeErrorMessage(t('promoCode.label.alreadyApplied'));
        } else {
            // 2. If customer already don't have promo code

            setIsLoading(true);
            const { data } = isUserLoggedInAsDealer
                ? await DealService.applyPromoCodeAgentPortal(promoCode, getSessionIdCookie())
                : await DealService.applyPromoCode(promoCode, getSessionIdCookie());
            setIsLoading(false);
            if (!data.success) {
                setIsVisibleSuccessMessage(false);
                if (data?.messageId && data?.messageId === ERROR_MESSAGE_IDS.AGENT_DISCOUNT_APPLIED) {
                    setPromoCodeErrorMessage(t('promoCode.label.error.agentDiscountApplied'));
                } else {
                    setPromoCodeErrorMessage(t('promoCode.label.error'));
                }

                return;
            }

            pushToDataLayer({
                event: 'uaevent',
                eventCategory: 'SolRetail::Basket',
                eventAction: 'Validate::PromoCode',
                eventLabel: buttonLabel,
            } as GtmData);

            setPromoCodeCookie(promoCode);
            dispatch(CarDetailsDuck.setCurrentDeal(data.deal));
            dispatch(CarDetailsDuck.setCurrentCarDetails(data.deal.fullProductConfiguration));
            dispatch(FinanceWidgetDuck.setFinanceQuote(data.financeQuote));
            setIsVisibleSuccessMessage(true);
            setPromoCodeErrorMessage(null);
            dispatch(GlobalDuck.setPromoCodeError(false));
            setPromoCode(null);
        }
    };

    useEffect(() => {
        if (promoCodeError) {
            setIsVisibleSuccessMessage(false);
            setPromoCodeErrorMessage(false);
        }

        setPromoCode(null);
    }, [promoCodeError]);

    const hasMonthlyPrices = hasMonthlyPriceByType(currentDeal?.fullProductConfiguration?.prices, EMPLOYEE_PRICE_TYPE);
    const hasSelectedPaymentMethod = currentDeal?.extraFields?.hasSelectedPaymentJourney || !hasMonthlyPrices;

    return (
        <div className={`${className} promoCode`}>
            {!hasSelectedPaymentMethod && validateSelectedPaymentMethod && (
                <p className="promoCodeInfo">{t('promoCode.info')}</p>
            )}
            <div className="promoCodeWrapper">
                {showTitle && <p className="promoCode__title">{t('promoCode.title')}</p>}
                <div className="promoCode__input_container">
                    <input
                        className={`promoCode__input ${promoCodeErrorMessage ? 'promoCode__input_error' : ''}`}
                        placeholder={t('promoCode.placeholder')}
                        onChange={(e) => setPromoCode(e.target.value)}
                        value={promoCode || ''}
                        data-testid="TESTING_PROMO_CODE_INPUT"
                    />
                    {promoCodeErrorMessage && (
                        <span className="promoCode__input_label_error" data-testid="TESTING_PROMO_CODE_ERROR_MESSAGE">
                            {promoCodeErrorMessage}
                        </span>
                    )}
                    {isVisibleSuccessMessage && (
                        <span
                            className="promoCode__input_label_success"
                            data-testid="TESTING_PROMO_CODE_SUCCESS_MESSAGE"
                        >
                            {page === Pages.basketV2 && (
                                <Icon
                                    width={20}
                                    height={10}
                                    name={Icons.Check}
                                    onClick={() => setIsVisibleSuccessMessage(false)}
                                />
                            )}
                            <span>{t('promoCode.label.success')}</span>
                            {page !== Pages.basketV2 && (
                                <Icon
                                    width={25}
                                    height={13}
                                    name={Icons.Cancel}
                                    onClick={() => setIsVisibleSuccessMessage(false)}
                                />
                            )}
                        </span>
                    )}
                </div>
                <button
                    className={`promoCode__button ${isLoading ? 'isLoading' : ''}`}
                    disabled={isVisibleSuccessMessage || (!hasSelectedPaymentMethod && validateSelectedPaymentMethod)}
                    onClick={applyPromoCode}
                    data-testid="TESTING_PROMO_CODE_BUTTON"
                >
                    {isLoading && <Spinner size={25} border={3} />}
                    {buttonLabel}
                </button>
            </div>
        </div>
    );
};

export default PromoCodeTemplate;
